import { defineComponent, h } from "vue";
import { FullScreenSheet, LemmonModal, SegmentSelect } from "@lemmonhq/uikit";
import { CameraPanel } from "./CameraPanel";
import { GiftCardPanel } from "./GiftCardPanel";
import { MembershipPanel } from "./MembershipPanel";
import { RewardsPanel } from "./RewardsPanel";
import { SummaryPanel } from "./SummaryPanel";
import { useLoyaltyModal } from "./useLoyaltyModal";
import type { LoyaltyModalProps, ScanType } from "./types";
export type { ResolvedCheckoutCustomer } from "./types";

export const LoyaltyModal = defineComponent<LoyaltyModalProps>({
  name: "LoyaltyModal",
  props: [
    "isOpen",
    "onClose",
    "totalAmount",
    "onApply",
    "onCustomerResolved",
    "onCustomerRemoved",
    "remainingTotal",
    "initialScanType",
  ],
  setup(props) {
    const {
      t,
      mode,
      reset,
      existing,
      resolve,
      preview,
      loading,
      onScan,
      error,
      email,
      giftCode,
      lookupGift,
      card,
      amount,
      currency,
      money,
      applyAmount,
      selected,
      displayOptions,
      savings,
      payable,
      saving,
      save,
      close,
      mobile,
      missingSelections,
      invalidSelection,
      draft,
      services,
    } = useLoyaltyModal(props);
    const body = () => (
      <div class="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div class="min-h-0 flex-1 overflow-y-auto bg-base-100 p-4 sm:p-6">
          <fieldset disabled={saving.value} class="min-w-0 space-y-5">
            <div class="flex justify-center">
              <SegmentSelect
                value={mode.value}
                onChange={(v: ScanType) => {
                  reset();
                  mode.value = v;
                  if (v === "membership" && existing.value?.profile_id)
                    resolve(existing.value.profile_id);
                }}
                options={[
                  { value: "membership", label: t("loyalty.rewards.customer") },
                  { value: "gift_card", label: t("loyalty.rewards.gift_card") },
                ]}
              />
            </div>
            {mode.value === "membership" && preview.value ? (
              <>
                <div class="flex items-center gap-3 rounded-2xl border border-base-300 bg-base-200/50 p-4 shadow-sm">
                  <span
                    class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary font-bold text-primary-content shadow-sm"
                    aria-hidden="true"
                  >
                    {(preview.value.display_name || "?")
                      .slice(0, 1)
                      .toUpperCase()}
                  </span>
                  <div class="min-w-0 flex-1">
                    <p class="mb-1 text-[10px] font-bold uppercase tracking-widest text-base-content/40">
                      {t("loyalty.rewards.customer")}
                    </p>
                    <p class="break-words text-base font-bold leading-snug text-base-content">
                      {preview.value.display_name ||
                        t("loyalty.modal.customer_identified")}
                    </p>
                  </div>
                  <button
                    type="button"
                    class="btn btn-ghost btn-sm shrink-0 text-primary"
                    onClick={reset}
                  >
                    <span class="material-symbols-outlined text-base">
                      swap_horiz
                    </span>
                    {t("loyalty.rewards.change")}
                  </button>
                  {existing.value && (
                    <button
                      type="button"
                      class="btn btn-ghost btn-sm btn-circle shrink-0 text-base-content/60"
                      aria-label={t("loyalty.rewards.remove_customer")}
                      title={t("loyalty.rewards.remove_customer")}
                      onClick={() => {
                        props.onCustomerRemoved?.();
                        props.onClose();
                      }}
                    >
                      <span class="material-symbols-outlined">
                        person_remove
                      </span>
                    </button>
                  )}
                </div>
                <div class="grid gap-5 md:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)] md:items-start">
                  <RewardsPanel
                    preview={preview.value}
                    options={displayOptions.value}
                    selected={selected.value}
                    currency={currency.value}
                    checkout={draft.value}
                    services={services.value}
                    maximumValue={payable.value + savings.value}
                    onChange={(selections) => {
                      selected.value = selections;
                    }}
                  />
                  <div class="hidden md:block">
                    <SummaryPanel
                      options={displayOptions.value}
                      selected={selected.value}
                      savings={savings.value}
                      payable={payable.value}
                      subtotal={props.remainingTotal ?? props.totalAmount}
                      currency={currency.value}
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
                <div class="grid items-start gap-6 md:grid-cols-[1.15fr_1fr]">
                  {!card.value && (
                    <CameraPanel
                      key={mode.value}
                      onScan={onScan}
                      loading={loading.value}
                      loadingLabel={t(
                        mode.value === "membership"
                          ? "loyalty.modal.resolving_member"
                          : "loyalty.rewards.gift_loading",
                      )}
                    />
                  )}
                  {card.value && (
                    <div class="flex min-h-64 flex-col items-center justify-center gap-4 rounded-2xl border border-primary/20 bg-primary/5 p-6">
                      <span class="material-symbols-outlined text-5xl text-primary">
                        card_giftcard
                      </span>
                      <h3 class="text-lg font-semibold">
                        {t("loyalty.rewards.gift_found")}
                      </h3>
                      <p class="text-3xl font-semibold tabular-nums">
                        {money(Number(card.value.current_balance))}
                      </p>
                    </div>
                  )}
                  <div class="space-y-6 py-2 md:py-5">
                    <div class="hidden space-y-2 md:block">
                      <p class="text-xs font-semibold uppercase tracking-widest text-primary">
                        {t("loyalty.rewards.identify")}
                      </p>
                      <h3 class="text-2xl font-semibold tracking-tight">
                        {t(
                          mode.value === "membership"
                            ? "loyalty.rewards.scan_title"
                            : "loyalty.rewards.gift_card",
                        )}
                      </h3>
                      <p class="text-sm leading-relaxed text-base-content/60">
                        {t(
                          mode.value === "membership"
                            ? "loyalty.rewards.identify_hint"
                            : "loyalty.rewards.scan_hint",
                        )}
                      </p>
                    </div>
                    {mode.value === "membership" ? (
                      <MembershipPanel
                        loading={loading.value}
                        error={error.value}
                        email={email.value}
                        onEmail={(v) => (email.value = v)}
                        onEmailLookup={() => resolve()}
                        onReset={reset}
                      />
                    ) : (
                      <>
                        <form
                          class="flex flex-col gap-2 sm:flex-row"
                          onSubmit={(e) => {
                            e.preventDefault();
                            lookupGift(giftCode.value.trim());
                          }}
                        >
                          <input
                            aria-label={t("loyalty.rewards.gift_code")}
                            placeholder={t("loyalty.rewards.gift_code")}
                            class="input input-bordered h-12 min-h-12 w-full min-w-0 shrink-0 sm:flex-1"
                            disabled={loading.value}
                            value={giftCode.value}
                            onInput={(e) =>
                              (giftCode.value = (
                                e.target as HTMLInputElement
                              ).value)
                            }
                          />
                          <button
                            type="submit"
                            class="btn btn-primary"
                            disabled={!giftCode.value.trim() || loading.value}
                          >
                            {t("loyalty.modal.look_up")}
                          </button>
                        </form>
                        <GiftCardPanel
                          cardInfo={card.value}
                          loading={loading.value}
                          error={error.value}
                          customAmount={amount.value}
                          currency={currency.value}
                          remaining={props.remainingTotal ?? props.totalAmount}
                          onCustomAmount={(v) => (amount.value = v)}
                          onReset={reset}
                        />
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
            {missingSelections.value.length > 0 && (
              <div role="alert" class="alert alert-warning">
                <span>{t("loyalty.rewards.availability_changed")}</span>
                <button
                  type="button"
                  class="btn btn-sm"
                  onClick={() =>
                    (selected.value = selected.value.filter(
                      (id) => !missingSelections.value.includes(id),
                    ))
                  }
                >
                  {t("loyalty.rewards.clear_unavailable")}
                </button>
              </div>
            )}
            {preview.value && error.value && (
              <p role="alert" class="alert alert-error">
                {error.value}
              </p>
            )}
          </fieldset>
        </div>
        <footer
          class="flex shrink-0 flex-col gap-4 border-t border-base-300 bg-base-100 p-4 sm:flex-row sm:items-center sm:px-6"
          style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
        >
          {mode.value === "membership" && preview.value && (
            <div
              class="flex flex-1 items-center gap-5 text-sm md:hidden"
              aria-live="polite"
            >
              <div class="flex items-center gap-2">
                <span class="material-symbols-outlined text-lg text-success">
                  savings
                </span>
                <div>
                  <p class="mb-0.5 text-xs leading-none text-base-content/50">
                    {t("loyalty.rewards.savings")}
                  </p>
                  <p class="font-bold leading-tight tabular-nums text-success">
                    −{money(savings.value)}
                  </p>
                </div>
              </div>
              <div class="border-l border-base-300 pl-5">
                <p class="mb-0.5 text-xs leading-none text-base-content/50">
                  {t("loyalty.rewards.payable")}
                </p>
                <p class="font-bold leading-tight tabular-nums text-base-content">
                  {money(payable.value)}
                </p>
              </div>
            </div>
          )}
          <div class="flex gap-2 sm:min-w-56 sm:justify-end md:ml-auto">
            {preview.value || card.value ? (
              <button
                type="button"
                class="btn btn-primary min-h-12 h-auto flex-1 gap-1.5 whitespace-normal px-5 py-3 sm:max-w-60"
                disabled={
                  saving.value ||
                  loading.value ||
                  (mode.value === "membership"
                    ? !preview.value || invalidSelection.value
                    : card.value?.status !== "active" || applyAmount.value <= 0)
                }
                onClick={save}
              >
                {saving.value && (
                  <span class="loading loading-spinner loading-xs" />
                )}
                {!saving.value && (
                  <span class="material-symbols-outlined text-sm">check</span>
                )}
                {mode.value === "gift_card"
                  ? `${t("loyalty.rewards.apply")} ${money(applyAmount.value)}`
                  : t(
                      preview.value &&
                        preview.value.profile_id === existing.value?.profile_id
                        ? "loyalty.rewards.save"
                        : "loyalty.rewards.attach_apply",
                    )}
              </button>
            ) : (
              <span class="flex flex-1 items-center justify-end gap-2 text-sm text-base-content/60">
                <span class="material-symbols-outlined text-primary">
                  qr_code_scanner
                </span>
                {t("loyalty.rewards.scan_hint")}
              </span>
            )}
          </div>
        </footer>
      </div>
    );
    return () =>
      !props.isOpen ? null : mobile.value ? (
        <FullScreenSheet
          isOpen={props.isOpen}
          canClose={() => !saving.value}
          onClose={close}
        >
          <div
            class="flex h-full min-h-0 flex-col bg-base-100"
            style={{ paddingTop: "var(--safe-area-top, 0px)" }}
          >
            <header class="flex shrink-0 items-center gap-3 border-b border-base-300 px-4 py-3">
              <button
                type="button"
                class="btn btn-ghost btn-sm btn-circle"
                aria-label={t("general.words.back")}
                disabled={saving.value}
                onClick={close}
              >
                <span class="material-symbols-outlined">arrow_back</span>
              </button>
              <h2 class="min-w-0 font-semibold">
                {t("loyalty.rewards.customer_rewards")}
              </h2>
            </header>
            {body()}
          </div>
        </FullScreenSheet>
      ) : (
        <LemmonModal
          style="max-width: 960px;"
          title={t("loyalty.rewards.customer_rewards")}
          onClose={close}
        >
          <div class="flex max-h-[calc(100dvh-150px)] min-h-0 flex-col">
            {body()}
          </div>
        </LemmonModal>
      );
  },
});
